'use strict';

(() => {
  function getButtonStatusEl(button) {
    if (!button) return null;

    const statusId = button.getAttribute('aria-controls');
    if (!statusId) return null;

    const form = button.closest('form');
    return (
      form?.querySelector(`#${CSS.escape(statusId)}`) ||
      form?.parentElement?.querySelector(`#${CSS.escape(statusId)}`) ||
      document.getElementById(statusId)
    );
  }

  function announceButtonStatus(button, message, immediate = false) {
    if (!message) return;

    function getLiveEl(parent, name) {
      let el = parent.querySelector(`[data-cart-live="${name}"]`);
      if (!el) {
        el = document.createElement('div');
        el.setAttribute('data-cart-live', name);
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('aria-atomic', 'true');
        el.style.cssText =
          'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
        parent.appendChild(el);
      }
      return el;
    }

    let statusEl = null;
    if (button) {
      const statusId = button.getAttribute('aria-controls');
      if (statusId) {
        statusEl = getButtonStatusEl(button);

        if (!statusEl && button.isConnected) {
          statusEl = document.createElement('span');
          statusEl.id = statusId;
          statusEl.className = 'visually-hidden';
          statusEl.setAttribute('role', 'status');
          statusEl.setAttribute('aria-live', 'polite');
          statusEl.setAttribute('aria-atomic', 'true');
          const component = button.closest('add-to-cart-component');
          if (component) component.appendChild(statusEl);
          else button.after(statusEl);
        }
      }
    }

    if (statusEl) {
      statusEl.textContent = '';
      if (immediate) {
        statusEl.textContent = message;
        return;
      }

      setTimeout(() => {
        statusEl.textContent = message;
        setTimeout(() => {
          if (statusEl.isConnected) {
            statusEl.textContent = '';
          }
        }, 3000);
      }, 50);
      return;
    }

    if (!immediate) {
      const globalLive = getLiveEl(document.body, 'global');
      const dropdownCart = document.getElementById('dropdown-cart');
      const modalLive = dropdownCart ? getLiveEl(dropdownCart, 'modal') : null;

      [globalLive, modalLive].forEach((el) => {
        if (el) {
          el.textContent = '';
        }
      });

      if (window._cartAnnounceClearTimer) clearTimeout(window._cartAnnounceClearTimer);

      setTimeout(() => {
        const isMinicartOpen = document.documentElement.classList.contains('cart-show') || (dropdownCart && !dropdownCart.classList.contains('hidden'));
        const targetLive = isMinicartOpen && modalLive ? modalLive : globalLive;
        if (targetLive) {
          targetLive.textContent = message;
          window._cartAnnounceClearTimer = setTimeout(() => {
            targetLive.textContent = '';
          }, 3000);
        }
      }, 50);
    }
  }

  function focusWhenReady(el) {
    if (!el?.isConnected) return;

    const run = () => {
      if (!el.isConnected || el.hasAttribute('disabled')) return;

      if (el.getAttribute('tabindex') === '-1') el.tabIndex = 0;
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      el.focus({ preventScroll: true });
    };

    run();
    requestAnimationFrame(run);
    setTimeout(run, 100);
    setTimeout(run, 300);
  }

  function ensureToggleSideCartHook() {
    if (!window._theme?.toggleSideCart || window._theme.toggleSideCart.__qvFocusRestore) return;

    const original = window._theme.toggleSideCart;

    window._theme.toggleSideCart = function (show, callback, isAutoAction) {
      if (show === false && (window._theme.lastQuickviewTrigger || window._theme.lastCartTrigger)) {
        const trigger = window._theme.lastQuickviewTrigger || window._theme.lastCartTrigger;
        window._theme.lastQuickviewTrigger = null;
        window._theme.lastCartTrigger = null;

        original.call(
          this,
          show,
          () => {
            focusWhenReady(trigger);
            if (typeof callback === 'function') callback();
          },
          isAutoAction
        );
        return;
      }

      return original.call(this, show, callback, isAutoAction);
    };

    window._theme.toggleSideCart.__qvFocusRestore = true;
  }

  function getFormData(form) {
    const formData = new FormData(form);

    const id = Number(formData.get('id'));
    if (!id) return null;

    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);

    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        properties[key.slice(11, -1)] = value;
      }
    }

    return {
      id,
      quantity,
      ...(Object.keys(properties).length ? { properties } : {}),
    };
  }

  async function addToCart(items, isBuyNow = false) {
    const url = window.routes?.cart_add_url || '/cart/add.js';

    if (isBuyNow) {
      window._theme = window._theme || {};
      window._theme.isBuyNowClick = true;
    } else if (window._theme) {
      window._theme.isBuyNowClick = false;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: `gwp,minicart${isBuyNow ? ',isBuyNow' : ''}`,
        sections_url: '/'
      }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {}

    if (!response.ok) {
      const message = data?.description || data?.message || 'Add to cart failed';

      window._theme?.Helpers?.showNotification?.(message, 3000);

      throw new Error(message);
    }

    const handle = data?.items?.[0]?.handle || data?.handle;
    if (handle) {
      document.dispatchEvent(
        new CustomEvent('cart:item_added', {
          detail: { handle, cart: data },
        })
      );
    }

    window._theme?.initListrakCartUpdateEvent?.();

    return data;
  }

  const ATC_ADDED_MIN_MS = 250;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setAtcAddedState(button, isAdded) {
    if (!button) return;
    if (isAdded) {
      button.dataset.added = 'true';
      button.setAttribute('aria-busy', 'true');
    } else {
      delete button.dataset.added;
      button.removeAttribute('aria-busy');
    }
  }

  function waitAtcAnimation(startedAt) {
    const minMs = prefersReducedMotion() ? 150 : ATC_ADDED_MIN_MS;
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, minMs - elapsed);
    return new Promise(function (resolve) {
      window.setTimeout(resolve, remaining);
    });
  }

  class AddToCartComponent extends HTMLElement {
    connectedCallback() {
      this.handleAddToCart = this.handleAddToCart.bind(this);

      this.form = this.closest('form');
      this.button = this.querySelector('[data-add-to-cart-btn]');

      if (!this.form || !this.button) return;

      this.button.addEventListener('click', this.handleAddToCart);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleAddToCart);
    }

    async handleAddToCart(event) {
      event.preventDefault();

      if (this.button.disabled || this.button.dataset.added === 'true') return;

      if (window._theme && this.button) {
        window._theme.lastCartTrigger = this.button;
      }

      const quickView = this.closest('quickview-dialog');
      const useTickAnimation = !!this.button.querySelector('.add-to-cart__added');

      announceButtonStatus(this.button, `Please wait, adding to the cart...`);

      const formData = getFormData(this.form);
      if (!formData) return;

      this.button.disabled = true;
      const animationStartedAt = Date.now();

      if (useTickAnimation) {
        setAtcAddedState(this.button, true);
      } else {
        window._theme?.toggleLoading?.(1);
      }

      try {
        const data = await addToCart([formData]);
        const productName =
          data?.items?.[0]?.product_title ||
          data?.items?.[0]?.title ||
          data?.product_title ||
          data?.title ||
          'Product';

        if (useTickAnimation) {
          await waitAtcAnimation(animationStartedAt);
        }

        if (quickView) {
          this.openMinicart(data, productName, quickView);
        } else {
          announceButtonStatus(this.button, `Added ${productName} to cart`);
          this.openMinicart(data);
          if (useTickAnimation) {
            window.setTimeout(() => setAtcAddedState(this.button, false), prefersReducedMotion() ? 150 : 400);
          }
        }
      } catch (err) {
        console.error(err);
        announceButtonStatus(this.button, err?.message || 'Unable to add product to cart');
        setAtcAddedState(this.button, false);
      } finally {
        if (!quickView) {
          this.button.disabled = false;
        } else if (this.button.dataset.added !== 'true') {
          this.button.disabled = false;
        }
        if (!useTickAnimation) {
          window._theme?.toggleLoading?.();
        }
      }
    }

    openMinicart(data, productName, quickView) {
      if (!data?.sections?.minicart) {
        console.warn('Minicart section missing');
        setAtcAddedState(this.button, false);
        this.button.disabled = false;
        window._theme?.toggleSideCart?.(1, null, true);
        return;
      }

      const isQuickView = quickView && typeof quickView.closeDialog === 'function';
      const lineItemChange = window._theme?.MiniCart?.lineItemChangeCallBack;

      if (typeof lineItemChange !== 'function') {
        console.warn('MiniCart.lineItemChangeCallBack missing');
        setAtcAddedState(this.button, false);
        this.button.disabled = false;
        window._theme?.toggleSideCart?.(1, null, true);
        return;
      }

      lineItemChange(data, () => {
        if (!isQuickView) {
          window._theme?.toggleSideCart?.(1, null, true);
          return;
        }

        ensureToggleSideCartHook();

        const trigger = quickView.triggerButton;
        if (trigger && window._theme) {
          window._theme.lastQuickviewTrigger = trigger;
        }
        quickView.triggerButton = null;

        announceButtonStatus(this.button, `Added ${productName} to cart`, true);

        const openCart = () => {
          setAtcAddedState(this.button, false);
          this.button.disabled = false;
          window._theme?.toggleSideCart?.(1, null, true);
          focusWhenReady(document.querySelector('#dropdown-cart .exit.close-cart'));
        };

        setTimeout(() => {
          const dialogEl = quickView.querySelector('dialog');

          if (dialogEl?.open) {
            dialogEl.addEventListener('close', openCart, { once: true });
            quickView.closeDialog(false);
            return;
          }

          if (quickView.hasAttribute('open')) {
            quickView.closeDialog(true);
          }

          openCart();
        }, 250);
      });
    }
  }

  document.addEventListener('DOMContentLoaded', ensureToggleSideCartHook);

  if (!customElements.get('add-to-cart-component')) {
    customElements.define('add-to-cart-component', AddToCartComponent);
  }

  class BuyNowComponent extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.handleBuyNow = this.handleBuyNow.bind(this);

      this.form = this.closest('form');
      this.button = this.querySelector('[data-buy-now-btn]');

      if (!this.form || !this.button) return;

      this.button.addEventListener('click', this.handleBuyNow);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleBuyNow);
    }

    async handleBuyNow(event) {
      event.preventDefault();

      if (this.button.disabled) return;

      const formData = getFormData(this.form);
      if (!formData) return;

      this.button.disabled = true;

      try {
        await addToCart([formData], true);
        window.location.href = '/checkout';
      } catch (err) {
        console.error(err);
      } finally {
        this.button.disabled = false;
      }
    }
  }
  if (!customElements.get('buy-now-component')) {
    customElements.define('buy-now-component', BuyNowComponent);
  }
})();
